import React, { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import "./ReminderPopup.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const STORAGE_PREFIX = "ems_reminder_dismissed_";
const CHECK_INTERVAL_MS = 60 * 1000; // every 1 minute

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getReminderKey = (type, eventId, dateStr) =>
  `${STORAGE_PREFIX}${type}_${eventId}_${dateStr}`;

const isAlreadyDismissed = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const markAsDismissed = (key) => {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // ignore
  }
};

// Purge old dismissed keys (older than 7 days) to keep storage clean
const purgeOldDismissedKeys = () => {
  try {
    const today = moment().format("YYYY-MM-DD");
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        // key format: ems_reminder_dismissed_TYPE_ID_YYYY-MM-DD
        const parts = key.split("_");
        const dateStr = parts[parts.length - 1];
        if (
          dateStr &&
          dateStr.match(/^\d{4}-\d{2}-\d{2}$/) &&
          moment(dateStr).isBefore(moment(today).subtract(7, "days"))
        ) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

// Get event identifier robustly
const getEventId = (event) => {
  const raw = event;
  return (
    raw?.id ??
    raw?.Id ??
    raw?.calendar_event_id ??
    raw?.calendarEventId ??
    raw?.event_id ??
    raw?.eventId ??
    null
  );
};

// Get event title
const getEventTitle = (event) =>
  event?.Subject ||
  event?.title ||
  event?.name ||
  event?.eventName ||
  event?.upcomingEventName ||
  "Untitled";

// Get category (normalized)
const getCategory = (event) =>
  String(
    event?.category || event?.Category || event?.categoryName || "event"
  )
    .trim()
    .toLowerCase();

// Detect admin-created meeting events stored as 'reminder' or 'calendar'
// category (title starts with 'Meeting' or subject contains 'meeting')
const isMeetingEvent = (event, category) => {
  if (category === "meeting") return true;
  if (category === "reminder" || category === "calendar") {
    const rawTitle = String(
      event?.Subject || event?.title || event?.name || ""
    ).toLowerCase();
    // Matches titles like: "Meeting - X", "Reminder - meeting", "meeting ..."
    return (
      rawTitle.startsWith("meeting") ||
      rawTitle.includes("reminder - meeting") ||
      rawTitle.includes("meeting -")
    );
  }
  return false;
};

// ─── Reminder Builder ─────────────────────────────────────────────────────────
const buildReminders = (events = [], isAdmin = false, currentUserId = null) => {
  const now = moment();
  const todayStr = now.format("YYYY-MM-DD");
  const reminders = [];

  events.forEach((event) => {
    const category = getCategory(event);
    // Promote reminder/calendar events that are actually meetings
    const effectiveCategory = isMeetingEvent(event, category) ? "meeting" : category;
    const eventId = getEventId(event) ?? getEventTitle(event).replace(/\s+/g, "-").toLowerCase();
    // Strip 'Reminder - ' prefix for cleaner display in the popup
    const rawTitle = getEventTitle(event);
    const title = rawTitle.replace(/^Reminder\s*[-:]/i, "").trim() || rawTitle;

    // ── Birthday: same day (match month-day across any year) ──────────────────
    if (category === "birthday" || category === "bday") {
      const startMoment = moment(event.start || event.StartTime || event.date);
      if (!startMoment.isValid()) return;
      const thisYearBday = startMoment.clone().year(now.year());
      if (thisYearBday.format("MM-DD") === now.format("MM-DD")) {
        const key = getReminderKey("birthday", eventId, todayStr);
        if (!isAlreadyDismissed(key)) {
          reminders.push({
            id: `birthday-${eventId}-${todayStr}`,
            dismissKey: key,
            type: "birthday",
            category: "birthday",
            icon: "🎂",
            typeLabel: "Birthday Today",
            title,
            subtitle: "Wishing a very Happy Birthday! 🎉",
            timeChip: "Today",
            urgent: false,
            sortWeight: 10,
          });
        }
      }
      return;
    }

    // ── Holiday: 2 days before and 1 day before ───────────────────────────────
    if (effectiveCategory === "holiday") {
      const startMoment = moment(event.start || event.StartTime || event.date);
      if (!startMoment.isValid()) return;
      const diffDays = startMoment.clone().startOf("day").diff(now.clone().startOf("day"), "days");

      if (diffDays === 2 || diffDays === 1) {
        const reminderType = `holiday-${diffDays}d`;
        const key = getReminderKey(reminderType, eventId, todayStr);
        if (!isAlreadyDismissed(key)) {
          reminders.push({
            id: `${reminderType}-${eventId}-${todayStr}`,
            dismissKey: key,
            type: "holiday",
            category: "holiday",
            icon: "🏖️",
            typeLabel: "Upcoming Holiday",
            title,
            subtitle:
              diffDays === 1
                ? `Tomorrow — ${startMoment.format("ddd, MMM D")}`
                : `In 2 days — ${startMoment.format("ddd, MMM D")}`,
            timeChip: diffDays === 1 ? "Tomorrow" : "In 2 Days",
            urgent: false,
            sortWeight: 20,
          });
        }
      }
      return;
    }

    // ── Leave: 1 day before — for user OR admin ───────────────────────────────
    if (effectiveCategory === "leave") {
      const startMoment = moment(event.start || event.StartTime || event.date);
      if (!startMoment.isValid()) return;
      const diffDays = startMoment.clone().startOf("day").diff(now.clone().startOf("day"), "days");

      if (diffDays !== 1) return;

      // Determine whose leave this is
      const leaveOwnerId =
        event?.user_id ??
        event?.userId ??
        event?.UserId ??
        event?.created_by ??
        event?.createdBy ??
        null;

      const isMyLeave =
        currentUserId !== null &&
        leaveOwnerId !== null &&
        String(leaveOwnerId) === String(currentUserId);

      const shouldShow = isAdmin || isMyLeave;
      if (!shouldShow) return;

      // Get employee name from title or event data
      const leaveOwnerName =
        event?.firstName ||
        event?.userName ||
        event?.employeeName ||
        event?.employee_name ||
        null;

      const key = getReminderKey("leave-1d", eventId, todayStr);
      if (!isAlreadyDismissed(key)) {
        reminders.push({
          id: `leave-1d-${eventId}-${todayStr}`,
          dismissKey: key,
          type: "leave",
          category: "leave",
          icon: "🌿",
          typeLabel: isAdmin && !isMyLeave ? "Employee Leave Tomorrow" : "Your Leave Tomorrow",
          title: isAdmin && leaveOwnerName && !isMyLeave
            ? `${leaveOwnerName} — ${title}`
            : title,
          subtitle: `Starts tomorrow — ${startMoment.format("ddd, MMM D")}`,
          timeChip: "Tomorrow",
          urgent: false,
          sortWeight: 30,
        });
      }
      return;
    }

    // ── Meeting: 1 day before + 10 minutes before ─────────────────────────────
    if (effectiveCategory === "meeting") {
      const startMoment = moment(event.start || event.StartTime);
      if (!startMoment.isValid()) return;

      const diffDays = startMoment.clone().startOf("day").diff(now.clone().startOf("day"), "days");
      const minutesUntil = startMoment.diff(now, "minutes");

      // Only show for meeting participants + admin
      const rawTargetUsers =
        event?.target_user_ids ||
        event?.TargetUserIds ||
        event?.targetUserIds ||
        [];
      let targetIds = [];
      if (Array.isArray(rawTargetUsers)) {
        targetIds = rawTargetUsers.map((id) => String(id));
      } else if (typeof rawTargetUsers === "string") {
        try {
          targetIds = JSON.parse(rawTargetUsers).map((id) => String(id));
        } catch {
          targetIds = rawTargetUsers
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        }
      }

      const showToAll = Boolean(
        event?.showToAll ?? event?.show_to_all ?? event?.ShowToAll
      );
      const isParticipant =
        isAdmin ||
        showToAll ||
        (currentUserId !== null && targetIds.includes(String(currentUserId)));

      if (!isParticipant) return;

      // 1 day before
      if (diffDays === 1) {
        const key = getReminderKey("meeting-1d", eventId, todayStr);
        if (!isAlreadyDismissed(key)) {
          reminders.push({
            id: `meeting-1d-${eventId}-${todayStr}`,
            dismissKey: key,
            type: "meeting",
            category: "meeting",
            icon: "📅",
            typeLabel: "Meeting Tomorrow",
            title,
            subtitle: `Tomorrow at ${startMoment.format("h:mm A")}`,
            timeChip: "Tomorrow",
            urgent: false,
            sortWeight: 40,
          });
        }
      }

      // Same day, within 10 minutes before (or just started — up to 0 minutes)
      if (minutesUntil >= 0 && minutesUntil <= 10) {
        const soonKey = getReminderKey("meeting-soon", eventId, todayStr);
        if (!isAlreadyDismissed(soonKey)) {
          reminders.push({
            id: `meeting-soon-${eventId}-${todayStr}`,
            dismissKey: soonKey,
            type: "meeting-soon",
            category: "meeting-soon",
            icon: "⚡",
            typeLabel: minutesUntil === 0 ? "Meeting Starting Now!" : "Meeting Starting Soon!",
            title,
            subtitle:
              minutesUntil === 0
                ? "The meeting is starting right now!"
                : `Starts in ${minutesUntil} minute${minutesUntil === 1 ? "" : "s"}`,
            timeChip:
              minutesUntil === 0
                ? "Now!"
                : `${minutesUntil} min`,
            urgent: true,
            sortWeight: 1,
          });
        }
      }
      return;
    }
  });

  // Sort: urgent first, then by sortWeight
  reminders.sort((a, b) => {
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
    return a.sortWeight - b.sortWeight;
  });

  return reminders;
};

// ─── Bell Icon SVG ─────────────────────────────────────────────────────────
const BellIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  </svg>
);

// ─── Main ReminderPopup Component ────────────────────────────────────────────
const ReminderPopup = ({ events = [], isAdmin = false, currentUser = null }) => {
  const [reminders, setReminders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const intervalRef = useRef(null);

  const currentUserId = currentUser?.id ?? currentUser?.Id ?? null;

  const computeReminders = useCallback(() => {
    const computed = buildReminders(events, isAdmin, currentUserId);
    setReminders(computed);
  }, [events, isAdmin, currentUserId]);

  // Initial load + purge old keys
  useEffect(() => {
    purgeOldDismissedKeys();
    computeReminders();
  }, [computeReminders]);

  // Poll every minute to catch the "10 min before meeting" trigger
  useEffect(() => {
    intervalRef.current = setInterval(computeReminders, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [computeReminders]);

  // Auto-open panel when NEW urgent (meeting-soon) reminder appears
  useEffect(() => {
    const hasUrgent = reminders.some((r) => r.urgent);
    if (hasUrgent) {
      setIsOpen(true);
    }
  }, [reminders]);

  const handleDismiss = (reminder) => {
    markAsDismissed(reminder.dismissKey);
    setReminders((prev) => prev.filter((r) => r.id !== reminder.id));
  };

  const handleDismissAll = () => {
    reminders.forEach((r) => markAsDismissed(r.dismissKey));
    setReminders([]);
    setIsOpen(false);
  };

  const togglePanel = () => setIsOpen((prev) => !prev);

  const unreadCount = reminders.length;

  return (
    <>
      {/* Bell Button */}
      <button
        className="ems-reminder-bell-btn"
        onClick={togglePanel}
        title="Reminders"
        aria-label={`Reminders (${unreadCount} pending)`}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="ems-reminder-bell-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Overlay + Panel */}
      {isOpen && (
        <>
          <div
            className="ems-reminder-panel-overlay"
            onClick={() => setIsOpen(false)}
          />
          <div className="ems-reminder-panel" role="dialog" aria-label="Reminders panel">
            {/* Header */}
            <div className="ems-reminder-panel-header">
              <div className="ems-reminder-panel-header-left">
                <div className="ems-reminder-panel-header-icon">🔔</div>
                <div>
                  <h3>Reminders</h3>
                  <p>
                    {unreadCount === 0
                      ? "All caught up!"
                      : `${unreadCount} pending reminder${unreadCount > 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>
              <div className="ems-reminder-panel-header-actions">
                {unreadCount > 0 && (
                  <button
                    className="ems-reminder-panel-clear-btn"
                    onClick={handleDismissAll}
                    title="Dismiss all reminders"
                  >
                    Clear All
                  </button>
                )}
                <button
                  className="ems-reminder-panel-close-btn"
                  onClick={() => setIsOpen(false)}
                  title="Close panel"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="ems-reminder-panel-body">
              {reminders.length === 0 ? (
                <div className="ems-reminder-empty">
                  <div className="ems-reminder-empty-icon">✅</div>
                  <p>No reminders right now.</p>
                  <p style={{ fontSize: "12px", marginTop: "4px", color: "#b0bec5" }}>
                    Check back later!
                  </p>
                </div>
              ) : (
                reminders.map((reminder) => (
                  <ReminderCard
                    key={reminder.id}
                    reminder={reminder}
                    onDismiss={handleDismiss}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ─── ReminderCard ─────────────────────────────────────────────────────────────
const ReminderCard = ({ reminder, onDismiss }) => {
  return (
    <div className={`ems-reminder-card ems-reminder-card-${reminder.category}`}>
      <div className="ems-reminder-card-stripe" />
      <div className="ems-reminder-card-inner">
        <div className="ems-reminder-card-row1">
          <span className="ems-reminder-card-icon">{reminder.icon}</span>
          <div className="ems-reminder-card-text">
            <div className="ems-reminder-card-type-label">{reminder.typeLabel}</div>
            <div className="ems-reminder-card-title">{reminder.title}</div>
            {reminder.subtitle && (
              <div className="ems-reminder-card-subtitle">{reminder.subtitle}</div>
            )}
          </div>
          <button
            className="ems-reminder-card-dismiss-btn"
            onClick={() => onDismiss(reminder)}
            title="Dismiss this reminder"
            aria-label="Dismiss reminder"
          >
            ✕
          </button>
        </div>
        <div className="ems-reminder-card-footer">
          <span className="ems-reminder-card-time-chip">
            {reminder.urgent ? "⚡" : "🕐"} {reminder.timeChip}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReminderPopup;
